import { z } from "zod";
import { caseInsensitivePattern, type JsonSchemaObject } from "./json-schema.ts";
import { PersonRef } from "./people.ts";

export const GITLAB_ACCESS_LEVELS = ["developer", "owner"] as const;
export const GITLAB_ACCESS_LEVEL_OPTIONS = [
  { id: "developer", label: "Developer" },
  { id: "owner", label: "Owner" },
] as const;

export const GitLabAccessLevel = z.enum(GITLAB_ACCESS_LEVELS);
export type GitLabAccessLevel = z.infer<typeof GitLabAccessLevel>;

const GITLAB_PATH_PATTERN = /^[A-Za-z0-9_][A-Za-z0-9_.-]*(?:\/[A-Za-z0-9_][A-Za-z0-9_.-]*)*$/u;

const GitLabPathBase = z
  .string()
  .regex(/^[\x20-\x7e]*$/u)
  .trim()
  .min(1)
  .max(800)
  .regex(GITLAB_PATH_PATTERN);

// Role groups are `role-gitlab-<path without secfo>-<level>`; the check lives on submitted input only so stored answers with a legacy prefix still parse.
const GitLabSubmittedPath = GitLabPathBase.refine((path) => !/^secfo\//iu.test(path), { message: "Enter the group path without the secfo/ prefix", params: { userFacing: true } });

export const GitLabAccessConfig = z
  .object({
    accessLevels: z
      .array(GitLabAccessLevel)
      .min(1)
      .max(GITLAB_ACCESS_LEVELS.length)
      .refine((levels) => new Set(levels).size === levels.length, "Access levels must be unique")
      .default([...GITLAB_ACCESS_LEVELS]),
    includeOwnersInWebhook: z.boolean().default(false),
  })
  .strict();
export type GitLabAccessConfig = z.infer<typeof GitLabAccessConfig>;

export const GitLabAccessSelection = z.object({ path: GitLabSubmittedPath, accessLevel: GitLabAccessLevel }).strict();
export type GitLabAccessSelection = z.infer<typeof GitLabAccessSelection>;

const GitLabAccessWireSelection = GitLabAccessSelection.extend({ version: z.literal(1) }).strict();
const GitLabAccessBrowser = z.object({ id: z.string().min(1).max(1_000) }).strict();

export function encodeGitLabAccessSelection(selection: GitLabAccessSelection): { id: string } {
  const parsed: GitLabAccessSelection = GitLabAccessSelection.parse(selection);
  return GitLabAccessBrowser.parse({ id: JSON.stringify({ version: 1, ...parsed }) });
}

export function decodeGitLabAccessSelection(value: unknown): GitLabAccessSelection {
  const browser: { id: string } = GitLabAccessBrowser.parse(value);
  const wire: z.infer<typeof GitLabAccessWireSelection> = GitLabAccessWireSelection.parse(JSON.parse(browser.id));
  return { path: wire.path, accessLevel: wire.accessLevel };
}

export const GitLabAccessMaterialized = z
  .object({
    path: GitLabPathBase,
    accessLevel: GitLabAccessLevel,
    provider: z.literal("gitlab"),
    roleGroupId: z.string().trim().min(1).max(200),
    roleGroupName: z.string().trim().min(1).max(1_000),
    owners: z.array(PersonRef),
    ownersResolvedAt: z.string().datetime(),
  })
  .strict();
export type GitLabAccessMaterialized = z.infer<typeof GitLabAccessMaterialized>;

/** The JSON Schema has no trim step, so unlike the server it also rejects surrounding whitespace. */
export const gitLabAccessTargetValueJsonSchema = (accessLevels: readonly GitLabAccessLevel[]): JsonSchemaObject => ({
  type: "object",
  properties: {
    path: { type: "string", minLength: 1, maxLength: 800, pattern: GITLAB_PATH_PATTERN.source, not: { pattern: `^${caseInsensitivePattern("secfo")}/` } },
    accessLevel: { enum: [...accessLevels] },
  },
  required: ["path", "accessLevel"],
  additionalProperties: false,
});
